/**
 * What can be filtered and shown, per subject. Built from the registry so every module's facets,
 * attributes and record fields appear as they land, with their own labels. Pure: the server uses it to
 * validate and run, the web uses it to build, the agent uses it to describe.
 */
import {
  ANIMAL_CLASS, ANIMAL_SEX, ANIMAL_STATUS, ENTITY_FIELDS, FIN_KIND, GROUP_KIND, PLACE_KIND, STOCK_KIND, UNIT_LABELS,
  type EventTypeDef, type Field, type Fields, type Registry, type SubjectKind,
} from "../../../contract";
import { getPath, inferType } from "./values";
import type { ColumnAgg, FilterOp, FilterSummary, GroupLabel, Grain, ReportDefinition, ReportFilter, ReportSubject, ValueType } from "./types";

export interface FieldSpec {
  id: string;
  /** English catalog key. */
  label: string;
  type: ValueType;
  options?: Record<string, string>;
  ref_kind?: SubjectKind | "member" | "event";
  /** Picker heading (English key). */
  section: string;
  level: "core" | "more";
  filter: boolean;
  column: boolean;
  /** Record-count columns on cattle: how to summarize the records. */
  aggs?: ColumnAgg[];
  /** How the value is worked out, shown in "How this was worked out". */
  basis?: string;
  /** The module that tracks this isn't on this ranch yet. */
  unavailable?: boolean;
}

export interface CatalogOptions {
  eventTypes?: string[];
  grain?: Grain;
  canFinance: boolean;
}

const ORIGIN = (ENTITY_FIELDS.animal.origin as Extract<Field, { kind: "enum" }>).optionLabels;
const SOURCES = { capture: "Hondo bar", manual: "Form", import: "Import", device: "Device", partner: "Partner", system: "Hondo" };
const TASK_STATUS = { open: "To do", done: "Done", cancelled: "Cancelled" };
const REPEATS = { daily: "Daily", weekly: "Weekly", monthly: "Monthly", yearly: "Yearly" };
const FIN_STATUS = { draft: "Draft", open: "Unpaid", paid: "Paid", partial: "Partly paid", void: "Void" };

const spec = (id: string, label: string, type: ValueType, section: string, level: "core" | "more", extra: Partial<FieldSpec> = {}): FieldSpec =>
  ({ id, label, type, section, level, filter: true, column: true, ...extra });

const ANIMAL = "Animal";

export const ANIMAL_FIELDS: FieldSpec[] = [
  spec("id", "Animal", "ref", ANIMAL, "more", { ref_kind: "animal", column: false }),
  spec("tag", "Tag", "tag", ANIMAL, "core"),
  spec("name", "Name", "text", ANIMAL, "core"),
  spec("class", "Class", "enum", ANIMAL, "core", { options: ANIMAL_CLASS, basis: "Class is worked out from sex, age and calving history unless someone set it." }),
  spec("sex", "Sex", "enum", ANIMAL, "core", { options: ANIMAL_SEX }),
  spec("age_years", "Age in years", "number", ANIMAL, "core", { basis: "Age is counted from the birth date to the report date." }),
  spec("age_months", "Age in months", "number", ANIMAL, "more", { basis: "Age is counted from the birth date to the report date." }),
  spec("age_days", "Age in days", "number", ANIMAL, "more", { basis: "Age is counted from the birth date to the report date." }),
  spec("birth_date", "Birth date", "date", ANIMAL, "core"),
  spec("breed", "Breed", "text", ANIMAL, "core"),
  spec("location", "Place", "ref", ANIMAL, "core", { ref_kind: "place" }),
  spec("groups", "Groups", "refs", ANIMAL, "core", { ref_kind: "group" }),
  spec("color", "Color", "text", ANIMAL, "more"),
  spec("location_since", "In this place since", "date", ANIMAL, "more"),
  spec("status", "Status", "enum", ANIMAL, "more", { options: ANIMAL_STATUS }),
  spec("origin", "Origin", "enum", ANIMAL, "more", { options: ORIGIN }),
  spec("castrated", "Castrated", "boolean", ANIMAL, "more"),
  spec("dam", "Dam", "ref", ANIMAL, "more", { ref_kind: "animal" }),
  spec("sire", "Sire", "ref", ANIMAL, "more", { ref_kind: "animal" }),
  spec("lot", "Lot", "ref", ANIMAL, "more", { ref_kind: "group" }),
  spec("eid", "EID", "text", ANIMAL, "more"),
  spec("registration", "Registration number", "text", ANIMAL, "more"),
  spec("offspring", "Offspring recorded", "number", ANIMAL, "more", { basis: "Offspring counts calves in Hondo with this animal as dam or sire, born by the report date." }),
  spec("notes", "Notes", "text", ANIMAL, "more"),
];

/**
 * Keys other modules publish in their facets (see MODULES.md, shared facet shapes). Offered only when
 * that facet exists on this ranch's registry; a facet's own `filters` win when both describe a path.
 */
export const KNOWN_FACET_PATHS: Array<{ facet: string; path: string; label: string; type: ValueType; options?: Record<string, string>; level?: "core" | "more"; ref_kind?: SubjectKind }> = [
  { facet: "breeding", path: "status", label: "Breeding status", type: "enum", level: "core", options: { open: "Open, not pregnant", exposed: "Exposed", bred: "Bred", pregnant: "Pregnant", calved: "Calved", unknown: "Unknown" } },
  { facet: "breeding", path: "due_date", label: "Calving due date", type: "date", level: "core" },
  { facet: "breeding", path: "last_calved", label: "Last calved", type: "date", level: "core" },
  { facet: "breeding", path: "last_service.on", label: "Last bred", type: "date" },
  { facet: "breeding", path: "last_service.sire_id", label: "Service sire", type: "ref", ref_kind: "animal" },
  { facet: "breeding", path: "calvings", label: "Times calved", type: "number" },
  { facet: "breeding", path: "has_calved", label: "Has calved", type: "boolean" },
  { facet: "health", path: "holds", label: "Withdrawal holds", type: "list", level: "core" },
  { facet: "health", path: "last_treatment.on", label: "Last treated", type: "date", level: "core" },
  { facet: "health", path: "last_treatment.product", label: "Last treatment", type: "text" },
  { facet: "health", path: "sick", label: "Sick", type: "boolean" },
  { facet: "performance", path: "last_weight", label: "Latest weight", type: "quantity", level: "core" },
  { facet: "performance", path: "last_weight.on", label: "Last weighed", type: "date" },
  { facet: "performance", path: "weights.birth", label: "Birth weight", type: "quantity" },
  { facet: "performance", path: "weights.weaning", label: "Weaning weight", type: "quantity", level: "core" },
  { facet: "performance", path: "weights.yearling", label: "Yearling weight", type: "quantity" },
  { facet: "performance", path: "adg", label: "Average daily gain", type: "number" },
  { facet: "performance", path: "bcs.score", label: "Body condition score", type: "number" },
  { facet: "genetics", path: "registration.number", label: "Registration number", type: "text" },
  { facet: "genetics", path: "registration.status", label: "Registration status", type: "text", level: "core" },
  { facet: "genetics", path: "dna.status", label: "DNA status", type: "text" },
  { facet: "herd", path: "arrival.on", label: "Arrived on", type: "date" },
  { facet: "herd", path: "departure.on", label: "Left the herd on", type: "date" },
  { facet: "herd", path: "departure.reason", label: "Why it left", type: "text" },
  { facet: "dairy", path: "status", label: "Dairy status", type: "enum", level: "core", options: { milking: "Milking", dry: "Dry", heifer: "Heifer" } },
  { facet: "dairy", path: "lactation", label: "Lactation number", type: "number" },
  { facet: "dairy", path: "dim", label: "Days in milk", type: "number" },
  { facet: "sales", path: "status", label: "Sale status", type: "enum", options: { listed: "Listed", reserved: "Reserved", sold: "Sold", delivered: "Delivered" } },
];

export function typeOfField(field: Field): { type: ValueType; options?: Record<string, string>; ref_kind?: SubjectKind | "member" } {
  switch (field.kind) {
    case "text": return { type: "text" };
    case "tag": return { type: "tag" };
    case "number": return { type: "number" };
    case "boolean": return { type: "boolean" };
    case "date": return { type: "date" };
    case "enum": return { type: "enum", options: field.optionLabels };
    case "quantity": return { type: "quantity" };
    case "money": return { type: "money" };
    case "ref": return { type: field.multiple ? "refs" : "ref", ref_kind: field.target === "fin_doc" ? undefined : field.target as SubjectKind | "member" };
    case "object": return { type: "text" };
    case "list": return { type: "list" };
  }
}

/** Every leaf field in an event payload, one level into objects. Lists of objects are skipped. */
export function dataPaths(fields: Fields, prefix = ""): Array<{ path: string; field: Field; label: string }> {
  const out: Array<{ path: string; field: Field; label: string }> = [];
  for (const [key, field] of Object.entries(fields)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (field.kind === "object") { if (!prefix) out.push(...dataPaths(field.fields, path)); continue; }
    if (field.kind === "list" && field.of.kind === "object") continue;
    out.push({ path, field, label: field.label });
  }
  return out;
}

export const visibleEventTypes = (reg: Registry, canFinance: boolean): EventTypeDef[] =>
  [...reg.events.values()].filter((e) => canFinance || !e.type.startsWith("finance."));

function moduleLabelForFacet(reg: Registry, facetId: string): string {
  return reg.modules.find((m) => m.facets?.some((f) => f.id === facetId))?.label ?? facetId;
}

function moduleLabelForType(reg: Registry, type: string): string {
  return reg.module(type.split(".")[0])?.label ?? type.split(".")[0];
}

function facetFields(reg: Registry, subject: SubjectKind): FieldSpec[] {
  const out = new Map<string, FieldSpec>();
  for (const facet of reg.facets.filter((f) => f.subject === subject)) {
    const section = moduleLabelForFacet(reg, facet.id);
    for (const [key, filter] of Object.entries(facet.filters ?? {})) {
      const type: ValueType = filter.type === "enum" ? "enum" : filter.type;
      out.set(`facet:${facet.id}.${key}`, spec(`facet:${facet.id}.${key}`, filter.label, type, section, "core", { options: filter.options }));
    }
    for (const k of KNOWN_FACET_PATHS.filter((x) => x.facet === facet.id)) {
      const id = `facet:${k.facet}.${k.path}`;
      if (!out.has(id)) out.set(id, spec(id, k.label, k.type, section, k.level ?? "more", { options: k.options, ref_kind: k.ref_kind }));
    }
  }
  return [...out.values()];
}

function attributeFields(reg: Registry, target: "animal" | "place" | "group"): FieldSpec[] {
  const out: FieldSpec[] = [];
  for (const m of reg.modules) {
    for (const [key, field] of Object.entries(m.attributes?.[target] ?? {})) {
      const t = typeOfField(field);
      out.push(spec(`attr:${m.id}.${key}`, field.label, t.type, m.label, field.level === "more" ? "more" : "core", { options: t.options, ref_kind: t.ref_kind }));
    }
  }
  return out;
}

/** Record-count and latest-value columns for cattle reports. */
function animalEventFields(reg: Registry, canFinance: boolean): FieldSpec[] {
  const out: FieldSpec[] = [];
  for (const def of visibleEventTypes(reg, canFinance)) {
    if (!def.subjects.some((s) => s.kind === "animal")) continue;
    const section = moduleLabelForType(reg, def.type);
    out.push(spec(`events:${def.type}`, def.label, "number", section, "more", { aggs: ["count", "latest", "first"], ref_kind: "event" }));
    for (const p of dataPaths(def.fields)) {
      const t = typeOfField(p.field);
      if (t.type === "refs" || t.type === "list") continue;
      const numericish = t.type === "number" || t.type === "quantity" || t.type === "money";
      out.push(spec(`events:${def.type}:${p.path}`, p.label, t.type, section, "more", {
        options: t.options, ref_kind: t.ref_kind, filter: false, aggs: numericish ? ["latest", "first", "sum", "avg", "min", "max", "count"] : ["latest", "first", "count"],
      }));
    }
  }
  return out;
}

export function eventTypesFor(def: Pick<ReportDefinition, "event_types">, reg: Registry, canFinance: boolean): EventTypeDef[] {
  const visible = visibleEventTypes(reg, canFinance);
  if (!def.event_types?.length) return visible;
  return def.event_types.map((t) => visible.find((e) => e.type === t)).filter((e): e is EventTypeDef => !!e);
}

export function defaultGrain(subject: ReportSubject): Grain {
  switch (subject) {
    case "animals": return "animal";
    case "events": return "event";
    case "groups": return "group";
    case "places": return "place";
    case "stock": return "lot";
    case "tasks": return "task";
    case "finance": return "doc";
  }
}

export function fieldCatalog(reg: Registry, subject: ReportSubject, opts: CatalogOptions): FieldSpec[] {
  const grain = opts.grain ?? defaultGrain(subject);
  switch (subject) {
    case "animals":
      return [...ANIMAL_FIELDS, ...facetFields(reg, "animal"), ...attributeFields(reg, "animal"), ...animalEventFields(reg, opts.canFinance)];
    case "events": {
      const types = eventTypesFor({ event_types: opts.eventTypes }, reg, opts.canFinance);
      const R = "About the record";
      const out: FieldSpec[] = [
        spec("event.date", "Date", "date", R, "core"),
        spec("event.type", "Record type", "enum", R, "core", { options: Object.fromEntries(types.map((e) => [e.type, e.label])) }),
        spec("event.summary", "What happened", "text", R, "core"),
        grain === "animal_event" ? spec("animal", "Animal", "ref", R, "core", { ref_kind: "animal" }) : spec("animals", "Animals", "refs", R, "core", { ref_kind: "animal" }),
        spec("subjects", "Who or what", "refs", R, "more"),
        spec("event.recorded_by", "Recorded by", "ref", R, "more", { ref_kind: "member" }),
        spec("event.recorded_at", "Recorded on", "date", R, "more"),
        spec("event.source", "How it was recorded", "enum", R, "more", { options: SOURCES }),
        spec("event.place", "Place", "ref", R, "more", { ref_kind: "place" }),
        spec("event.job", "Job", "ref", R, "more", { ref_kind: "job" }),
        spec("event.corrected", "Corrected", "boolean", R, "more"),
      ];
      const roles = new Map<string, FieldSpec>();
      const data = new Map<string, FieldSpec>();
      for (const def of types) {
        for (const s of def.subjects) {
          if (!roles.has(s.role)) roles.set(s.role, spec(`subject:${s.role}`, s.label, s.many ? "refs" : "ref", R, "more", { ref_kind: s.kind }));
        }
        for (const p of dataPaths(def.fields)) {
          const id = `data:${p.path}`;
          if (data.has(id)) continue;
          const t = typeOfField(p.field);
          data.set(id, spec(id, p.label, t.type, types.length === 1 ? def.label : "Record details", types.length === 1 && p.field.level !== "more" ? "core" : "more", { options: t.options, ref_kind: t.ref_kind }));
        }
      }
      const animal = ANIMAL_FIELDS.filter((f) => ["tag", "class", "sex", "age_years", "age_months", "breed", "location", "groups", "dam", "sire"].includes(f.id))
        .map((f) => ({ ...f, id: `animal:${f.id}`, level: "more" as const, section: ANIMAL }));
      return [...out, ...roles.values(), ...data.values(), ...animal];
    }
    case "groups": {
      const G = "Group";
      return [
        spec("name", "Name", "text", G, "core"),
        spec("kind", "Kind", "enum", G, "core", { options: GROUP_KIND }),
        spec("head", "Head", "number", G, "core", { basis: "Head counts current members that are in the herd." }),
        spec("counted_head", "Counted head", "number", G, "core"),
        spec("place", "Usual place", "ref", G, "core", { ref_kind: "place" }),
        spec("active", "Active", "boolean", G, "more"),
        ...attributeFields(reg, "group"),
      ];
    }
    case "places": {
      const P = "Place";
      return [
        spec("name", "Name", "text", P, "core"),
        spec("kind", "Kind", "enum", P, "core", { options: PLACE_KIND }),
        spec("head", "Head", "number", P, "core", { basis: "Head counts cattle in the herd whose current place is this one." }),
        spec("capacity", "Capacity (head)", "number", P, "core"),
        spec("use_pct", "Use of capacity (%)", "number", P, "core", { basis: "Use of capacity is head divided by capacity." }),
        spec("area", "Area", "quantity", P, "more"),
        spec("parent", "Part of", "ref", P, "more", { ref_kind: "place" }),
        ...facetFields(reg, "place"),
        ...attributeFields(reg, "place"),
      ];
    }
    case "stock": {
      const S = "Supplies";
      const kindFields = (pick: "fields" | "lotFields") => {
        const seen = new Map<string, FieldSpec>();
        for (const k of reg.stockKinds.values()) for (const [key, field] of Object.entries(k[pick] ?? {})) {
          if (seen.has(key)) continue;
          const t = typeOfField(field);
          seen.set(key, spec(`attr:${key}`, field.label, t.type, k.label, "more", { options: t.options, ref_kind: t.ref_kind }));
        }
        return [...seen.values()];
      };
      if (grain === "item") {
        return [
          spec("name", "Product", "text", S, "core"),
          spec("kind", "Kind", "enum", S, "core", { options: STOCK_KIND }),
          spec("on_hand", "On hand", "number", S, "core", { basis: "On hand adds every receipt, use and count recorded for the product." }),
          spec("unit", "Counted in", "text", S, "core"),
          spec("reorder_at", "Reorder at", "number", S, "core"),
          spec("below_reorder", "Below reorder point", "boolean", S, "core"),
          spec("lots", "Lots with stock", "number", S, "more"),
          spec("next_expiry", "Next to expire", "date", S, "core"),
          spec("used_30", "Used in the last 30 days", "number", S, "more"),
          ...kindFields("fields"),
        ];
      }
      return [
        spec("item", "Product", "ref", S, "core", { ref_kind: "stock_item" }),
        spec("kind", "Kind", "enum", S, "core", { options: STOCK_KIND }),
        spec("code", "Lot or batch", "text", S, "core"),
        spec("on_hand", "On hand", "number", S, "core", { basis: "On hand adds every receipt, use and count recorded for the lot." }),
        spec("unit", "Counted in", "text", S, "core"),
        spec("expires_at", "Expires", "date", S, "core"),
        spec("days_to_expiry", "Days until it expires", "number", S, "core", { basis: "Days until it expires are counted from the report date." }),
        spec("place", "Stored at", "ref", S, "more", { ref_kind: "place" }),
        spec("received_at", "Received", "date", S, "more"),
        ...kindFields("lotFields"),
        ...kindFields("fields"),
      ];
    }
    case "tasks": {
      const T = "Task";
      return [
        spec("title", "Task", "msg", T, "core"),
        spec("status", "Status", "enum", T, "core", { options: TASK_STATUS }),
        spec("due_at", "Due", "date", T, "core"),
        spec("days_overdue", "Days overdue", "number", T, "core", { basis: "Days overdue are counted from the due date to the report date for open tasks." }),
        spec("assignee", "Assigned to", "ref", T, "core", { ref_kind: "member" }),
        spec("subject", "About", "ref", T, "core"),
        spec("job", "Job", "ref", T, "more", { ref_kind: "job" }),
        spec("repeat", "Repeats", "enum", T, "more", { options: REPEATS }),
        spec("from_record", "Follow-up from a record", "boolean", T, "more"),
        spec("completed_at", "Done on", "date", T, "more"),
        spec("created_at", "Created", "date", T, "more"),
      ];
    }
    case "finance": {
      if (!opts.canFinance) return [];
      const F = "Money";
      const common = [
        spec("date", "Date", "date", F, "core"),
        spec("kind", "Kind", "enum", F, "core", { options: FIN_KIND }),
        spec("contact", "With", "ref", F, "core", { ref_kind: "contact" }),
        spec("status", "Status", "enum", F, "more", { options: FIN_STATUS }),
        spec("number", "Number", "text", F, "more"),
        spec("memo", "Memo", "text", F, "core"),
      ];
      if (grain === "line") {
        return [
          ...common,
          spec("description", "Description", "text", F, "core"),
          spec("category", "Category", "text", F, "core"),
          spec("amount", "Amount", "money", F, "core"),
          spec("quantity", "Quantity", "number", F, "more"),
          spec("unit", "Unit", "text", F, "more"),
          spec("product", "Product", "ref", F, "more", { ref_kind: "stock_item" }),
        ];
      }
      return [
        ...common,
        spec("total", "Total", "money", F, "core"),
        spec("due_date", "Due", "date", F, "more"),
        spec("days_past_due", "Days past due", "number", F, "more", { basis: "Days past due are counted from the due date to the report date for unpaid records." }),
      ];
    }
  }
}

/**
 * Look up a field, including facet paths no contract describes ("facet:breeding.custom.path"). Those
 * read whatever is stored and are marked unavailable when the facet isn't registered at all.
 */
export function resolveField(reg: Registry, catalog: FieldSpec[], subject: ReportSubject, id: string, sample?: unknown): FieldSpec | null {
  const known = catalog.find((f) => f.id === id);
  if (known) return known;
  const facetMatch = /^facet:([A-Za-z0-9_-]+)\.(.+)$/.exec(id);
  if (facetMatch && (subject === "animals" || subject === "places")) {
    const [, facetId, path] = facetMatch;
    const registered = reg.facets.some((f) => f.id === facetId && f.subject === (subject === "animals" ? "animal" : "place"));
    const known = KNOWN_FACET_PATHS.find((k) => k.facet === facetId && k.path === path);
    return spec(id, known?.label ?? path.split(".").at(-1)!.replace(/_/g, " "), known?.type ?? (sample !== undefined ? inferType(sample) : "text"), facetId, "more", {
      options: known?.options, ref_kind: known?.ref_kind, unavailable: !registered,
    });
  }
  const animalOnEvent = /^animal:(.+)$/.exec(id);
  if (animalOnEvent && subject === "events") {
    const base = ANIMAL_FIELDS.find((f) => f.id === animalOnEvent[1]);
    return base ? { ...base, id, level: "more" } : null;
  }
  return null;
}

/** Operators that make sense for a value type. */
export function opsFor(f: Pick<FieldSpec, "type" | "aggs" | "id">): FilterOp[] {
  if (f.id.startsWith("events:")) return ["exists", "not_exists", "count_gte"];
  switch (f.type) {
    case "tag":
    case "text":
    case "msg": return ["eq", "neq", "contains", "starts_with", "in", "is_set", "is_not_set"];
    case "enum": return ["eq", "neq", "in", "not_in", "is_set", "is_not_set"];
    case "number":
    case "quantity":
    case "money": return ["gt", "gte", "lt", "lte", "between", "eq", "neq", "is_set", "is_not_set"];
    case "date": return ["within_last_days", "older_than_days", "within_next_days", "before", "after", "between", "eq", "is_set", "is_not_set"];
    case "boolean": return ["is_true", "is_false", "is_set", "is_not_set"];
    case "ref": return ["eq", "neq", "in", "not_in", "is_set", "is_not_set"];
    case "refs": return ["in", "not_in", "is_set", "is_not_set"];
    case "list": return ["contains", "is_set", "is_not_set"];
    case "summary": return ["contains"];
  }
}

/** Short operator labels for the builder (English keys). */
export const OP_LABELS: Record<FilterOp, string> = {
  eq: "is", neq: "is not", in: "is any of", not_in: "is none of", contains: "contains", starts_with: "starts with",
  gt: "over", gte: "at least", lt: "under", lte: "at most", between: "between",
  before: "before", after: "after", within_last_days: "in the last days", older_than_days: "more than days ago", within_next_days: "in the next days",
  is_set: "is recorded", is_not_set: "is not recorded", is_true: "yes", is_false: "no",
  exists: "has at least one", not_exists: "has none", count_gte: "has at least",
};

/** Record summary labels for record-count columns (English keys). */
export const AGG_LABELS: Record<ColumnAgg, string> = {
  count: "How many", latest: "Latest", first: "First", sum: "Total", avg: "Average", min: "Lowest", max: "Highest",
};

export const NEEDS_VALUE: Record<FilterOp, "none" | "one" | "many" | "two" | "days"> = {
  eq: "one", neq: "one", in: "many", not_in: "many", contains: "one", starts_with: "one",
  gt: "one", gte: "one", lt: "one", lte: "one", between: "two",
  before: "one", after: "one", within_last_days: "days", older_than_days: "days", within_next_days: "days",
  is_set: "none", is_not_set: "none", is_true: "none", is_false: "none",
  exists: "none", not_exists: "none", count_gte: "one",
};

/** Plain-language description of one filter, translated later by the reader. */
export function summarizeFilter(filter: ReportFilter, f: FieldSpec | null, labels: Record<string, string>, custom?: string): FilterSummary {
  const values: GroupLabel[] = [];
  const add = (v: unknown) => {
    if (v === null || v === undefined) return;
    if (typeof v === "object" && !Array.isArray(v) && "value" in (v as object)) {
      const q = v as { value: number; unit: string };
      values.push({ text: String(q.value), type: "quantity", unit: q.unit });
      return;
    }
    const s = String(v);
    if (f?.type === "enum") values.push({ text: f?.options?.[s] ?? s, translate: true });
    else if (f?.type === "ref" || f?.type === "refs") values.push({ text: labels[s] ?? s });
    else if (f?.type === "date") values.push({ text: s, type: "date" });
    else if (typeof v === "number") values.push({ text: s, type: "number" });
    else values.push({ text: s });
  };
  if (Array.isArray(filter.value)) filter.value.forEach(add);
  else add(filter.value);
  return { field_label: custom ?? f?.label ?? filter.field, custom: !!custom, op: filter.op, values, days: filter.days };
}

/** Phrases for filter summaries. Keys are English catalog keys. */
export function filterPhrase(op: FilterOp, hasDays: boolean, days: number | undefined): string {
  const one = days === 1;
  switch (op) {
    case "eq":
    case "in": return "{{field}}: {{value}}";
    case "neq":
    case "not_in": return "{{field}}: not {{value}}";
    case "contains": return "{{field}}: contains {{value}}";
    case "starts_with": return "{{field}}: starts with {{value}}";
    case "gt": return "{{field}}: over {{value}}";
    case "gte": return "{{field}}: {{value}} or more";
    case "lt": return "{{field}}: under {{value}}";
    case "lte": return "{{field}}: {{value}} or less";
    case "between": return "{{field}}: {{from}} to {{to}}";
    case "before": return "{{field}}: before {{value}}";
    case "after": return "{{field}}: after {{value}}";
    case "within_last_days": return one ? "{{field}}: in the last day" : "{{field}}: in the last {{count}} days";
    case "older_than_days": return one ? "{{field}}: more than a day ago" : "{{field}}: more than {{count}} days ago";
    case "within_next_days": return one ? "{{field}}: in the next day" : "{{field}}: in the next {{count}} days";
    case "is_set": return "{{field}}: recorded";
    case "is_not_set": return "{{field}}: not recorded";
    case "is_true": return "{{field}}: yes";
    case "is_false": return "{{field}}: no";
    case "exists": return hasDays ? (one ? "{{field}}: at least one in the last day" : "{{field}}: at least one in the last {{count}} days") : "{{field}}: at least one";
    case "not_exists": return hasDays ? (one ? "{{field}}: none in the last day" : "{{field}}: none in the last {{count}} days") : "{{field}}: none";
    case "count_gte": return hasDays ? "{{field}}: {{value}} or more in the last {{count}} days" : "{{field}}: {{value}} or more";
  }
}

export interface PhraseFormat {
  t(text: string, values?: Record<string, unknown>): string;
  date(value: string): string;
  number(n: number): string;
}

export function labelText(l: GroupLabel, f: PhraseFormat): string {
  if (l.type === "date") return f.date(l.text);
  if (l.type === "number") return f.number(Number(l.text));
  if (l.type === "quantity") return `${f.number(Number(l.text))} ${f.t(UNIT_LABELS[l.unit ?? ""] ?? l.unit ?? "")}`.trim();
  return l.translate ? f.t(l.text) : l.text;
}

export function filterText(s: FilterSummary, f: PhraseFormat): string {
  const field = s.custom ? s.field_label : f.t(s.field_label);
  const parts = s.values.map((v) => labelText(v, f));
  return f.t(filterPhrase(s.op, s.days !== undefined, s.days), {
    field, value: parts.join(", "), from: parts[0] ?? "", to: parts[1] ?? "", count: s.days ?? 0,
  });
}

/** A readable label for a column: the person's heading, or the field label with its summary. */
export function columnLabelKey(f: FieldSpec | null, agg: ColumnAgg | undefined, fallback: string): { text: string; values?: Record<string, string>; field?: string } {
  const label = f?.label ?? fallback;
  if (!f?.id.startsWith("events:") || !agg) return { text: label };
  const isType = f.id.split(":").length === 2;
  if (isType) {
    if (agg === "count") return { text: "{{field}}: how many", field: label };
    if (agg === "latest") return { text: "{{field}}: last date", field: label };
    if (agg === "first") return { text: "{{field}}: first date", field: label };
  }
  const key: Record<ColumnAgg, string> = {
    count: "{{field}}: how many", latest: "{{field}}: latest", first: "{{field}}: first", sum: "{{field}}: total", avg: "{{field}}: average", min: "{{field}}: lowest", max: "{{field}}: highest",
  };
  return { text: key[agg], field: label };
}

export { getPath };
